interface DealVelocityStage {
  stageId: string;
  stageName: string;
  avgDays: number;
  dealCount: number;
  isBottleneck?: boolean;
  comparisonAvgDays?: number | null;
}

interface DealVelocityChartProps {
  stages: DealVelocityStage[];
  overallAvgDays: number;
  title?: string;
  emptyCopy?: string;
  comparisonLabel?: string;
}

export function DealVelocityChart({
  stages,
  overallAvgDays,
  title = "Deal velocity",
  emptyCopy = "No data",
  comparisonLabel = "avg",
}: DealVelocityChartProps) {
  if (stages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--color-border)] px-4 py-8 text-center text-sm text-[var(--color-muted-foreground)]">
        {emptyCopy}
      </div>
    );
  }

  const normalizedStages = stages.map((stage) => ({
    ...stage,
    comparisonAvgDays:
      stage.comparisonAvgDays == null ? (overallAvgDays > 0 ? overallAvgDays : null) : stage.comparisonAvgDays,
  }));
  const maxDays = Math.max(
    ...normalizedStages.map((stage) => Math.max(stage.avgDays, stage.comparisonAvgDays ?? 0)),
    1,
  );

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{title}</h3>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Average deal time by stage compared with {comparisonLabel}.
          </p>
        </div>
        <span className="rounded-full bg-[var(--color-muted)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)]">
          Pipeline avg {overallAvgDays.toFixed(1)}d
        </span>
      </div>

      <div className="space-y-3">
        {normalizedStages.map((stage) => {
          const comparison = stage.comparisonAvgDays;
          const isFasterThanAverage = comparison != null && stage.avgDays <= comparison;
          const barWidth = Math.max((stage.avgDays / maxDays) * 100, stage.avgDays > 0 ? 4 : 0);
          const comparisonWidth = comparison != null ? (comparison / maxDays) * 100 : 0;
          const barTone = stage.isBottleneck
            ? "bg-[var(--color-destructive)]/50"
            : isFasterThanAverage
              ? "bg-[var(--color-success)]/50"
              : "bg-[var(--color-warning)]/50";
          const labelTone = stage.isBottleneck
            ? "text-[var(--color-destructive)]"
            : isFasterThanAverage
              ? "text-[var(--color-success)]"
              : "text-[var(--color-warning)]";

          return (
            <div key={stage.stageId} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">{stage.stageName}</span>
                  {stage.isBottleneck ? (
                    <span className="rounded-full bg-[var(--color-destructive)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-destructive)]">
                      Bottleneck
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`font-medium ${labelTone}`}>{stage.avgDays.toFixed(1)}d</span>
                  <span className="text-[var(--color-muted-foreground)]">
                    {stage.dealCount > 0 ? `${stage.dealCount} deals` : "No data"}
                  </span>
                </div>
              </div>

              <div className="relative h-3 overflow-hidden rounded-full bg-[var(--color-muted)]">
                {comparison != null ? (
                  <div
                    className="absolute bottom-0 top-0 z-10 w-0.5 bg-[var(--color-muted-foreground)]"
                    style={{ left: `${comparisonWidth}%` }}
                    title={`${comparisonLabel}: ${comparison.toFixed(1)}d`}
                  />
                ) : null}
                <div className={`h-full rounded-full ${barTone}`} style={{ width: `${barWidth}%` }} />
              </div>

              <div className="flex items-center justify-between text-[10px] text-[var(--color-muted-foreground)]">
                <span>{comparison != null ? `${comparisonLabel}: ${comparison.toFixed(1)}d` : "No comparison baseline"}</span>
                <span>{stage.dealCount > 0 ? "" : "No data"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
